'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { FileCode2, Plus, Search, X } from 'lucide-react';
import type { SectionCategory } from '@/lib/storefront/section-templates';
import { SECTION_LIBRARY } from '@/lib/storefront/section-library';

const RECENT_KEY = 'fleetava.siteBuilder.recentSections';
const MAX_RECENT = 6;

type RecentEntry = { category: SectionCategory; variationId: string; name: string };

function readRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is RecentEntry =>
        !!item
        && typeof item === 'object'
        && typeof (item as RecentEntry).category === 'string'
        && typeof (item as RecentEntry).variationId === 'string'
        && typeof (item as RecentEntry).name === 'string')
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function pushRecent(entry: RecentEntry) {
  try {
    const next = [entry, ...readRecent().filter((item) => !(item.category === entry.category && item.variationId === entry.variationId))]
      .slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

interface SectionPickerProps {
  onAdd: (category: SectionCategory, variationId: string) => void;
  onClose?: () => void;
  embedded?: boolean;
  hiddenCategories?: SectionCategory[];
  /** When set, new sections insert at this canvas index (before that section). */
  insertHint?: number | null;
  onImportCustom?: () => void;
}

export function SectionPicker({
  onAdd,
  onClose,
  embedded = false,
  hiddenCategories = [],
  insertHint = null,
  onImportCustom,
}: SectionPickerProps) {
  const [activeCategory, setActiveCategory] = useState<SectionCategory>('hero');
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const available = SECTION_LIBRARY.filter((category) => !hiddenCategories.includes(category.id));
  const active = available.find((c) => c.id === activeCategory) ?? available[0] ?? SECTION_LIBRARY[0];

  useEffect(() => {
    setRecent(readRecent());
  }, []);

  const variations = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return active.variations;
    return active.variations.filter(
      (variation) =>
        variation.name.toLowerCase().includes(needle)
        || variation.description.toLowerCase().includes(needle),
    );
  }, [active.variations, query]);

  function handleAdd(category: SectionCategory, variationId: string, name: string) {
    pushRecent({ category, variationId, name });
    setRecent(readRecent());
    onAdd(category, variationId);
  }

  return (
    <div className="flex h-full flex-col bg-card text-ink">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">Section library</h2>
          <p className="text-[11px] text-muted">
            {insertHint === null
              ? 'Pick a block, then customise it on the canvas.'
              : insertHint === 0
                ? 'Next add inserts at the top of the page.'
                : `Next add inserts before section ${insertHint + 1}.`}
          </p>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-shell hover:text-ink" aria-label="Close library">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="space-y-2 border-b border-line px-3 py-2">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${active.label.toLowerCase()}…`}
            className="w-full rounded-lg border border-line bg-shell py-2 pl-8 pr-2.5 text-xs text-ink outline-none placeholder:text-muted focus:border-brand"
          />
        </label>
        {onImportCustom && (
          <button
            type="button"
            onClick={onImportCustom}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-2 text-[11px] font-semibold text-ink hover:bg-shell"
          >
            <FileCode2 className="h-3.5 w-3.5" />
            Import HTML / CSS / ZIP
          </button>
        )}
      </div>

      <div className="scrollbar-none grid grid-cols-4 gap-1 border-b border-line p-2">
        {SECTION_LIBRARY.map((category) => {
          if (hiddenCategories.includes(category.id)) return null;
          const Icon = category.icon;
          const isActive = category.id === active.id;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => {
                setActiveCategory(category.id);
                setQuery('');
              }}
              className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-[10px] font-semibold transition ${
                isActive ? 'bg-brand text-white' : 'text-muted hover:bg-shell hover:text-ink'
              }`}
              title={category.description}
              aria-label={category.label}
            >
              <Icon className="h-4 w-4" />
              <span className="leading-tight">{category.label}</span>
            </button>
          );
        })}
      </div>

      <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto p-3">
        {recent.length > 0 && !query && (
          <div className="mb-4">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted">Recently used</p>
            <div className="flex flex-wrap gap-1.5">
              {recent.map((entry) => (
                <button
                  key={`${entry.category}-${entry.variationId}`}
                  type="button"
                  onClick={() => handleAdd(entry.category, entry.variationId, entry.name)}
                  className="rounded-lg border border-line bg-shell/60 px-2 py-1 text-[11px] font-semibold text-ink hover:border-brand hover:bg-brand/10"
                >
                  {entry.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted">
          {active.label} · {variations.length} style{variations.length === 1 ? '' : 's'}
        </p>
        {variations.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line bg-shell/50 px-3 py-8 text-center text-xs text-muted">
            No templates match “{query}”.
          </p>
        ) : (
          <div className="space-y-3">
            {variations.map((variation) => {
              const Component = variation.component;
              return (
                <div key={variation.id} className="overflow-hidden rounded-xl border border-line bg-shell/40 transition hover:border-muted">
                  <div className="relative h-28 overflow-hidden border-b border-line bg-white">
                    <div
                      className="pointer-events-none origin-top-left"
                      style={{ width: '1000px', transform: 'scale(0.30)' }}
                      aria-hidden="true"
                    >
                      <Component content={variation.defaultContent} style={undefined} />
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-ink">{variation.name}</p>
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted">{variation.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAdd(active.id, variation.id, variation.name)}
                      className="shrink-0 rounded-lg bg-brand p-1.5 text-white hover:bg-brand/90"
                      aria-label={`Add ${variation.name}`}
                      title={
                        insertHint === null
                          ? `Add ${variation.name} at the end`
                          : `Add ${variation.name} at the chosen position`
                      }
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!embedded && (
        <div className="border-t border-line p-3 text-[11px] text-muted">
          {hiddenCategories.length > 0
            ? 'The header and footer are shared from the Home page.'
            : 'New sections insert at the chosen spot. Drag them on the canvas to reorder.'}
        </div>
      )}
    </div>
  );
}
